import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  DatePicker,
  Select,
  Upload,
  InputNumber,
  Collapse,
  Radio,
  Image,
} from "antd";
import { PlusCircleFilled, MinusCircleFilled, UploadOutlined } from "@ant-design/icons";
import { Loading, Report, Confirm } from "notiflix";
import moment from "moment";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";
import API from "../../../services";
import HeaderFarms from "../../../utils/header_farms";
import { requestToken } from "../../../auth/AuthServices";
import { useIsAuthenticated } from "@azure/msal-react";

import "./styles.css";

const { TextArea } = Input;

function CreatePPERequest() {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [token, setToken] = useState(null);
  const [uploadBase64, setUploadBase64] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Masters
  const [employees, setEmployees] = useState([]);
  const [activities, setActivities] = useState([]);
  const [hazards, setHazards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [employeeType, setEmployeeType] = useState("Employee");
  const [currentSession, setCurrentSession] = useState(
    JSON.parse(localStorage.getItem("session_signin"))
  );

  const [requestKind, setRequestKind] = useState("Baru"); // Baru | Penggantian | Peminjaman


  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const at = await requestToken();
        setToken(at);
        if (!at) return;

        // Load employees by branch (based on token)
        try {
          const empRes = await API.PPEEmployeesByBranch(HeaderFarms(at));
          const empData = empRes?.data || empRes || [];
          setEmployees(
            empData.map((e) => ({
              value: e.employee_id,
              label: `${e.fullname} (${e.employee_id})`,
              raw: e,
            }))
          );
        } catch (_) { }

        // Load master data
        try {
          const [act, haz, itm, cat] = await Promise.all([
            API.PPEActivities(HeaderFarms(at)),
            API.PPEHazardTypes(HeaderFarms(at)),
            API.PPEItems(HeaderFarms(at)),
            API.PPECategory(HeaderFarms(at)),
          ]);

          const activityArr = (act?.data || act || [])
            .map((x) => x.activity || x.name || x.category || x?.activity)
            .filter(Boolean);
          setActivities([...new Set(activityArr)]);

          const hazardArr = (haz?.data || haz || [])
            .map((x) => x.name || x.name || x?.name)
            .filter(Boolean);
          setHazards([...new Set(hazardArr)]);

          const categoryArr = (cat?.data || cat || [])
            .map((x) => x.category || x.name)
            .filter(Boolean);
          setCategories([...new Set(categoryArr)]);

          const itemsRaw = (itm?.data || itm || []).map((x) => ({ name: x.name, category: x.category, size: x.size || [], color: x.color || [] }));
          const uniqItems = [...new Map(itemsRaw.map((it) => [it.name, it])).values()];
          setItems(uniqItems);
          // sizes/colors now sourced only from selected APD item attributes
        } catch (err) {
          console.error(err);
        }

      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [isAuthenticated]);

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleUploadChange = ({ fileList }) => {
    const file = fileList[0];
    if (file && file.originFileObj) {
      toBase64(file.originFileObj)
        .then((b64) => {
          setUploadBase64(b64);
          form.setFieldsValue({ attachment: b64 });
        })
        .catch((e) => console.error(e));
    } else {
      setUploadBase64(null);
      form.setFieldsValue({ attachment: undefined });
    }
  };

  const onFinish = async (values) => {
    Confirm.show(
      "Important Confirmation",
      "Are you sure? Please make sure your request is correct.",
      "Yes",
      "No",
      async () => {
        try {
          setIsSubmitting(true);
          Loading.dots("Processing your request...");
          const payload = {
            employee_name: values.employee_name,
            employee_id: values.employee_id_requestor,
            department: values.department,
            submission_type: requestKind === "Baru" ? "Pengajuan Baru" : requestKind === "Penggantian" ? "Penggantian" : "Peminjaman",
            ppe_items: (values.ppe_items || []).map((i) => ({
              activity: i.activity,
              category: i.category,
              hazard_type: i.hazard_type,
              name: i.name,
              size: i.size,
              options: i.options,
              quantity: i.quantity,
              reason_replace: i.reason_replace,
              reason: i.reason,
            })),
            return_date: requestKind === "Peminjaman" && values.return_date ? moment(values.return_date).format("YYYY-MM-DD") : null,
            description: values.description || null,
            attachment: uploadBase64 || null,
            requestor_id: currentSession.nik,
          };

          const at = token || (await requestToken());
          console.log("PPE Request Payload:", currentSession.nik);
          await API.CreatePPERequest(HeaderFarms(at), payload);
          Report.success(
            "PPE Request",
            "Request berhasil dibuat",
            "Okay",
            () => navigate("/ppe/history")
          );
        } catch (err) {
          console.error(err);
          Report.warning("Oops.. something wrong", "Sorry, this application is experiencing problems", "Okay");
        } finally {
          setIsSubmitting(false);
          Loading.remove();
        }
      }
    );
  };

  return (
    <>
      <Navbar title="Create" subtitle="APD Request" back={true} navigate="/ppe/history" />
      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <div className="container mt-3 p-3">
          <Form form={form} layout="vertical" onFinish={onFinish} validateMessages={{ required: `\${label} wajib diisi`, types: { number: `\${label} harus berupa angka` }, number: { min: `\${label} minimal \${min}` } }} initialValues={{ ppe_items: [{}] }}>
            <Collapse defaultActiveKey={["1", "2"]}>
              <Collapse.Panel header={<b>Identitas Pemohon</b>} key="1">
                <Row>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Employee Status"
                      name="employee_type"
                      initialValue="Employee"
                    >
                      <Radio.Group
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmployeeType(value);

                          // reset field biar tidak nyangkut
                          form.resetFields([
                            "employee_id_requestor",
                            "employee_name",
                            "department",
                          ]);
                        }}
                      >
                        <Radio value="Employee">Employee</Radio>
                        <Radio value="Non Employee">Non Employee</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="ID Karyawan" tooltip="Data diambil dari Talenta" name="employee_id_requestor" rules={[{ required: true }]}>
                      {employeeType === "Employee" ? (
                        <Select
                          showSearch
                          loading={isLoading}
                          placeholder="Pilih Fullname"
                          options={employees}
                          filterOption={(input, option) =>
                            option?.label?.toLowerCase()?.includes(input.toLowerCase())
                          }
                          onChange={(_, opt) => {
                            if (!opt?.raw) return;
                            const e = opt.raw;
                            form.setFieldsValue({
                              employee_name: e.fullname,
                              department: e.organization_name,
                              employee_id_requestor: e.employee_id,
                            });
                          }}
                        />
                      ) : (
                        <Input placeholder="Masukkan Nama Non Employee" />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Nama" name="employee_name" rules={[{ required: true }]}>
                      <Input
                        disabled={employeeType === "Employee"}
                        placeholder="Nama"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Departemen" name="department" rules={[{ required: true }]}>
                      <Input disabled={employeeType === "Employee"} placeholder="Departemen" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Employee ID" name="employee_id_requestor" rules={[{ required: true }]}>
                      <Input style={{ pointerEvents: "none" }} placeholder="Employee ID" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col xs={24} md={12}>
                    <Form.Item tooltip="Pilih jenis permintaan" label="Jenis Permintaan" name="request_kind" initialValue={requestKind} rules={[{ required: true }]}>
                      <Radio.Group onChange={(e) => setRequestKind(e.target.value)}>
                        <Radio className="mt-3" value="Baru">Baru</Radio>
                        <Radio className="mt-3" value="Penggantian">Penggantian</Radio>
                        <Radio className="mt-3" value="Peminjaman">Peminjaman</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  {requestKind === "Peminjaman" && (
                    <Col xs={24} md={12}>
                      <Form.Item tooltip="Tanggal Pengembalian estimasi" label="Tanggal Pengembalian" name="return_date" rules={[{ required: true }]}>
                        <DatePicker format="YYYY-MM-DD" />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Row gutter={[16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Lampiran"
                      name="attachment"
                      rules={[
                        ({ getFieldValue }) => ({
                          required: getFieldValue('ppe_items')?.some(item => item?.reason_replace === 'Hilang'),
                          message: 'Lampiran wajib diisi jika alasan penggantian adalah Hilang.',
                        }),
                      ]}
                    >
                      <Upload
                        beforeUpload={() => false}
                        onChange={handleUploadChange}
                        maxCount={1}
                        accept=".jpg,.jpeg,.png"
                        showUploadList={false}
                      >
                        <Button icon={<UploadOutlined />} style={{ width: "100%" }}>Upload</Button>
                      </Upload>
                      <div style={{ marginTop: 10 }}>
                        <a href="/berita-acara-kehilangan.pdf" download>
                          Download Template Berita Acara Kehilangan
                        </a>
                      </div>
                      {uploadBase64 && (
                        <div style={{ marginTop: 10 }}>
                          <Image
                            src={uploadBase64}
                            alt="Attachment"
                            width={200}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Catatan" name="description">
                      <TextArea rows={3} />
                    </Form.Item>
                  </Col>
                </Row>
              </Collapse.Panel>

              <Collapse.Panel header={<b>Alat Pelindung Diri</b>} key="2">
                <Form.List name="ppe_items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => {
                        const itemName = form.getFieldValue(["ppe_items", name, "name"]);
                        const selectedCategory = form.getFieldValue(["ppe_items", name, "category"]);
                        const matchedItem = items.find((it) => it.name === itemName);

                        return (
                          <div key={key} className="ppe-item-panel">
                            <Row gutter={[16]}>
                              <Col xs={24} md={24}>
                                <Form.Item {...restField} name={[name, "name"]} label="Nama APD" rules={[{ required: true }]}>
                                  <Select
                                    showSearch
                                    loading={isLoading}
                                    placeholder={"Search by name APD"}
                                    options={items.map((it) => ({ value: it.name, label: it.name }))}
                                    filterOption={(i, o) => o?.label?.toLowerCase().includes(i.toLowerCase())}
                                    onChange={() => {
                                      const current = form.getFieldsValue();
                                      const list = current.ppe_items || [];
                                      const selectedItemName = form.getFieldValue(["ppe_items", name, "name"]);
                                      const selectedItem = items.find(it => it.name === selectedItemName);
                                      list[name] = { ...list[name], category: selectedItem?.category, size: undefined, options: undefined };
                                      form.setFieldsValue({ ppe_items: list });
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={[16]}>
                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "size"]}
                                  label="Ukuran"
                                  rules={[{
                                    required: !!(matchedItem && Array.isArray(matchedItem.size) && matchedItem.size.length > 0),
                                    message: "Ukuran wajib diisi",
                                  }]}
                                >
                                  <Select
                                    showSearch
                                    allowClear
                                    placeholder={matchedItem ? "Pilih ukuran" : "Pilih APD"}
                                    options={(matchedItem?.size || []).map((s) => ({ value: s, label: s }))}
                                    disabled={!matchedItem || (matchedItem?.size || []).length === 0}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item {...restField} name={[name, "options"]} label="Warna (Opsional)">
                                  <Select
                                    showSearch
                                    allowClear
                                    placeholder={matchedItem ? "Pilih warna" : "Pilih APD"}
                                    options={(matchedItem?.color || []).map((c) => ({ value: c, label: c }))}
                                    disabled={!matchedItem || (matchedItem?.color || []).length === 0}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={8}>
                                <Form.Item {...restField} name={[name, "quantity"]} label="Jumlah" rules={[{ required: true }]} initialValue={1}>
                                  <InputNumber min={1} style={{ width: "100%", pointerEvents: "none" }} />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={[16]}>
                              {requestKind === "Penggantian" && (
                                <Col xs={24} md={12}>
                                  <Form.Item {...restField} name={[name, "reason_replace"]} label="Alasan" rules={[{ required: true }]}>
                                    <Select placeholder="Pilih alasan" options={["Rusak", "Habis Masa Pakai", "Hilang"].map((r) => ({ value: r, label: r }))} />
                                  </Form.Item>
                                </Col>
                              )
                              }
                              {selectedCategory === "Khusus" && (
                                <Col xs={24} md={24}>
                                  <Form.Item {...restField} name={[name, "reason"]} label="Justifikasi (APD Khusus)" rules={[{ required: false }]}>
                                    <TextArea rows={2} placeholder="Justifikasi wajib diisi untuk kategori APD Khusus" />
                                  </Form.Item>
                                </Col>
                              )}
                            </Row>
                            <Row gutter={8} className="mb-2">
                              <Col>
                                <Button danger icon={<MinusCircleFilled />} type="primary" onClick={() => remove(name)} className="mb-3">
                                  Hapus Item
                                </Button>
                              </Col>
                              <Col>
                                <Button type="primary" onClick={() => add()} icon={<PlusCircleFilled />}>Tambah Item APD</Button>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                    </>
                  )}
                </Form.List>
              </Collapse.Panel>
            </Collapse>

            <Row className="justify-content-center mt-4">
              <Col>
                <Form.Item>
                  <Button loading={isSubmitting} type="primary" size="large" htmlType="submit">
                    Kirim Permintaan
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </div >
      <Footer />
    </>
  );
}

export default CreatePPERequest;